import { createReducer } from './utils';
import { translate as t } from '@/core/i18n/translate';
import {
  GET_REQUIREMENTS_REQUEST,
  GET_REQUIREMENTS_SUCCESS,
  GET_REQUIREMENTS_FAILURE,
  RESET_REQUIREMENTS_STATE,
  SNACK_SET,
} from './constants';
import { FocusRequirementWithDetails } from '@shared/schema';
import { RequirementsState, Action, ThunkAPI } from '../types/state';

const defaultState: RequirementsState = {
  requirements: [],
  loading: false,
  errorMessage: null,
};

export const reducer = createReducer(defaultState, {
  [GET_REQUIREMENTS_REQUEST]: handleGetRequirementsRequest,
  [GET_REQUIREMENTS_SUCCESS]: handleGetRequirementsSuccess,
  [GET_REQUIREMENTS_FAILURE]: handleGetRequirementsFailure,
  [RESET_REQUIREMENTS_STATE]: () => defaultState,
});

function handleGetRequirementsRequest(state: RequirementsState): RequirementsState {
  return {
    ...state,
    loading: true,
  };
}

function handleGetRequirementsSuccess(
  state: RequirementsState,
  action: Action<unknown>,
): RequirementsState {
  const payload = action.payload as { requirements: FocusRequirementWithDetails[] } | undefined;
  return {
    ...state,
    requirements: payload?.requirements || [],
    loading: false,
    errorMessage: null,
  };
}

function handleGetRequirementsFailure(
  state: RequirementsState,
  action: Action<unknown>,
): RequirementsState {
  const payload = action.payload as { errorMessage: string } | undefined;
  return {
    ...state,
    loading: false,
    errorMessage: payload?.errorMessage || t('apiFeedback.requirements.fetchError'),
  };
}

export function resetRequirementsState() {
  return (dispatch: ThunkAPI['dispatch']) => {
    dispatch({ type: RESET_REQUIREMENTS_STATE });
  };
}

export function getRequirements(params?: { planId?: number; year?: number }) {
  return async (
    dispatch: ThunkAPI['dispatch'],
    getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    dispatch({ type: GET_REQUIREMENTS_REQUEST });
    try {
      const requirements = await dataSource.getRequirements(params);
      dispatch({ type: GET_REQUIREMENTS_SUCCESS, payload: { requirements } });
    } catch (error) {
      console.error('Error fetching requirements:', error);
      const fallbackMessage = t('apiFeedback.requirements.fetchFailure');
      const errorMessage =
        error instanceof Error && error.message ? error.message : fallbackMessage;
      dispatch({
        type: GET_REQUIREMENTS_FAILURE,
        payload: { errorMessage },
      });
      const snack = {
        open: true,
        message: errorMessage,
        severity: 'error',
      };
      dispatch({ type: SNACK_SET, payload: { snack } });
    }
  };
}
